import React from 'react'
import type { RefDataItem } from '../api/refDataApi'
import {
  lookupsApi,
  type CountryLookup,
  type CityLookup,
  type LanguageLookup,
  type TimezoneLookup,
} from '../api/lookupsApi'
import { organizationTypesApi } from '@/modules/system/organization-types/api/organizationTypesApi'

type LookupState = {
  items: RefDataItem[]
  loading: boolean
  error: string | null
}

function toRefItem(
  groupKey: string,
  code: string,
  displayName: string,
  id: number,
  sortOrder = 0
): RefDataItem {
  return {
    id,
    groupKey,
    code,
    displayName,
    sortOrder,
    isActive: true,
  }
}

export function useLookups(selectedCountryIso2: string | null) {
  const [countries, setCountries] = React.useState<CountryLookup[]>([])
  const [cities, setCities] = React.useState<CityLookup[]>([])
  const [languages, setLanguages] = React.useState<LanguageLookup[]>([])
  const [timezones, setTimezones] = React.useState<TimezoneLookup[]>([])
  const [organizationTypes, setOrganizationTypes] = React.useState<RefDataItem[]>([])
  const [loading, setLoading] = React.useState({
    countries: true,
    cities: false,
    languages: true,
    timezones: true,
    organizationType: true,
  })
  const [error, setError] = React.useState<Record<string, string | null>>({
    country: null,
    city: null,
    language: null,
    timezone: null,
    organizationType: null,
  })

  // Fetch countries, languages, timezones once
  React.useEffect(() => {
    let cancelled = false
    const lang = 'tr'

    Promise.all([
      lookupsApi.getCountries({ lang }).catch((e) => (cancelled ? [] : Promise.reject(e))),
      lookupsApi.getLanguages({ lang }).catch((e) => (cancelled ? [] : Promise.reject(e))),
      lookupsApi.getTimezones().catch((e) => (cancelled ? [] : Promise.reject(e))),
      organizationTypesApi.list({ lang, pageSize: 100 }).catch((e) => (cancelled ? { items: [] } : Promise.reject(e))),
    ])
      .then(([c, l, t, otRes]) => {
        if (cancelled) return
        setCountries(Array.isArray(c) ? c : [])
        setLanguages(Array.isArray(l) ? l : [])
        setTimezones(Array.isArray(t) ? t : [])
        const otItems = otRes?.items ?? []
        setOrganizationTypes(
          otItems.map((o) =>
            toRefItem('organizationType', o.code, o.name, o.id, o.sortOrder)
          )
        )
        setLoading((s) => ({ ...s, countries: false, languages: false, timezones: false, organizationType: false }))
        setError((e) => ({ ...e, country: null, language: null, timezone: null, organizationType: null }))
      })
      .catch((e) => {
        if (cancelled) return
        setLoading((s) => ({ ...s, countries: false, languages: false, timezones: false, organizationType: false }))
        setError((s) => ({
          ...s,
          country: e?.message ?? 'Veriler alınamadı',
          language: e?.message ?? 'Veriler alınamadı',
          timezone: e?.message ?? 'Veriler alınamadı',
          organizationType: e?.message ?? 'Veriler alınamadı',
        }))
      })

    return () => {
      cancelled = true
    }
  }, [])

  // Fetch cities when selected country changes
  React.useEffect(() => {
    const iso2 = (selectedCountryIso2 ?? '').trim()
    if (!iso2) {
      setCities([])
      setLoading((s) => ({ ...s, cities: false }))
      setError((s) => ({ ...s, city: null }))
      return
    }

    const country = countries.find((c) => c.iso2 === iso2)
    if (!country) {
      setCities([])
      setLoading((s) => ({ ...s, cities: false }))
      return
    }

    let cancelled = false
    setLoading((s) => ({ ...s, cities: true }))
    setError((s) => ({ ...s, city: null }))

    lookupsApi
      .getCities(country.id, { lang: 'tr' })
      .then((list) => {
        if (cancelled) return
        setCities(Array.isArray(list) ? list : [])
        setLoading((s) => ({ ...s, cities: false }))
        setError((s) => ({ ...s, city: null }))
      })
      .catch((e) => {
        if (cancelled) return
        setCities([])
        setLoading((s) => ({ ...s, cities: false }))
        setError((s) => ({ ...s, city: e?.message ?? 'Veriler alınamadı' }))
      })

    return () => {
      cancelled = true
    }
  }, [selectedCountryIso2, countries])

  const refGroups: Record<string, LookupState> = {
    country: {
      items: countries.map((c, i) =>
        toRefItem('country', c.iso2, c.name, c.id, i + 1)
      ),
      loading: loading.countries,
      error: error.country,
    },
    city: {
      items: cities.map((c, i) =>
        toRefItem('city', c.name, c.name, c.id, i + 1)
      ),
      loading: loading.cities,
      error: error.city,
    },
    language: {
      items: languages.map((l, i) =>
        toRefItem('language', l.code, l.name, l.id, i + 1)
      ),
      loading: loading.languages,
      error: error.language,
    },
    timezone: {
      items: timezones.map((t, i) =>
        toRefItem('timezone', t.iana, `${t.iana} (${t.utcOffset})`, t.id, i + 1)
      ),
      loading: loading.timezones,
      error: error.timezone,
    },
    organizationType: {
      items: organizationTypes,
      loading: loading.organizationType,
      error: error.organizationType,
    },
  }

  return refGroups
}
